import React from 'react';
import { ColorSwatch } from '../../ColorSwatch';
import { TypographyScale } from '../../TypographyScale';
import { Check, X, Camera, Aperture, MapPin, Heart, Film } from 'lucide-react';
export function BrandDossierPage() {
  return <div className="max-w-5xl mx-auto py-12 px-8 space-y-16" data-id="element-409">
      {/* Header */}
      <div data-id="element-410">
        <p className="font-mono text-xs text-film-gray mb-3" data-id="element-411">
          01_BRAND_DOSSIER
        </p>
        <h1 className="font-display font-bold text-4xl text-film-black mb-2" data-id="element-412">
          Brand Dossier
        </h1>
        <p className="text-film-gray" data-id="element-413">
          Social-first slice. Positioning, voice, and the rules that make us
          look like us.
        </p>
      </div>

      {/* Positioning */}
      <section data-id="element-414">
        <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-3" data-id="element-415">
          Positioning
        </p>
        <div className="bg-film-black text-white p-10 relative overflow-hidden" data-id="element-416">
          <div className="absolute right-0 top-0 opacity-5" data-id="element-417">
            <Film className="w-64 h-64" data-id="element-418" />
          </div>
          <p className="font-display font-bold text-3xl md:text-4xl leading-tight relative z-10 max-w-3xl" data-id="element-419">
            Film Notes is the film photography hub — rolls, gear, locations, lab
            discovery, and progress in one calm, intentional iOS app.
          </p>
          <p className="text-white/60 mt-6 relative z-10" data-id="element-420">
            Not a notes app. Not just a logger. A place where the analog
            workflow comes together.
          </p>
        </div>
      </section>

      {/* TOFU Messages */}
      <section data-id="element-421">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-422">
          TOFU Messages
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-423">
          Rotate these — don't cram them all into one post.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-424">
          {[{
          label: 'Memory / Ritual',
          message: 'Never lose what you shot.',
          icon: <Camera className="w-4 h-4" data-id="element-425" />
        }, {
          label: 'Coherence',
          message: 'Rolls, gear, labs — one hub.',
          icon: <Aperture className="w-4 h-4" data-id="element-426" />
        }, {
          label: 'Craft',
          message: 'Built for film, not generic notes.',
          icon: <Film className="w-4 h-4" data-id="element-427" />
        }, {
          label: 'Privacy / Offline',
          message: 'Your workflow, your device.',
          icon: <MapPin className="w-4 h-4" data-id="element-428" />
        }].map(m => <div key={m.label} className="bg-white border border-film-border p-5 relative" data-id="element-429">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-film-red" data-id="element-430"></div>
              <div className="text-film-red mb-3" data-id="element-431">{m.icon}</div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-2" data-id="element-432">
                {m.label}
              </p>
              <p className="font-display font-bold text-film-black text-lg leading-tight" data-id="element-433">
                "{m.message}"
              </p>
            </div>)}
        </div>
      </section>

      {/* Personas */}
      <section data-id="element-434">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-435">
          Personas
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-436">
          Two lead audiences. Voice and creative shift between them.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-437">
          {/* Persona A */}
          <div className="bg-white border border-film-border p-6" data-id="element-438">
            <div className="flex items-baseline justify-between mb-4" data-id="element-439">
              <div data-id="element-440">
                <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-441">
                  Persona A
                </p>
                <h3 className="font-display font-bold text-2xl text-film-black" data-id="element-442">
                  Ritual Hobbyist
                </h3>
              </div>
              <span className="bg-film-stored text-film-red text-xs px-2 py-1 font-mono" data-id="element-443">
                18–30
              </span>
            </div>
            <dl className="space-y-3 text-sm" data-id="element-444">
              <div data-id="element-445">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-446">
                  Gear
                </dt>
                <dd className="text-film-black" data-id="element-447">
                  35mm, half-frame, instant-curious
                </dd>
              </div>
              <div data-id="element-448">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-449">
                  Platforms
                </dt>
                <dd className="text-film-black" data-id="element-450">Instagram, TikTok, Reddit</dd>
              </div>
              <div data-id="element-451">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-452">
                  Jobs
                </dt>
                <dd className="text-film-black" data-id="element-453">
                  Remember stock in camera. Share one scan with context. Avoid
                  ruined rolls.
                </dd>
              </div>
              <div data-id="element-454">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-455">
                  Triggers
                </dt>
                <dd className="text-film-black" data-id="element-456">
                  Tired of screenshots. Wants a ritual when loading film.
                  Inspired by peers.
                </dd>
              </div>
              <div data-id="element-457">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-458">
                  Creative angle
                </dt>
                <dd className="text-film-black" data-id="element-459">
                  User scans + UI, UGC prompts, chill Stories (BTS).
                </dd>
              </div>
            </dl>
          </div>

          {/* Persona B */}
          <div className="bg-white border border-film-border p-6" data-id="element-460">
            <div className="flex items-baseline justify-between mb-4" data-id="element-461">
              <div data-id="element-462">
                <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-463">
                  Persona B
                </p>
                <h3 className="font-display font-bold text-2xl text-film-black" data-id="element-464">
                  Serious / Hybrid Shooter
                </h3>
              </div>
              <span className="bg-film-stored text-film-red text-xs px-2 py-1 font-mono" data-id="element-465">
                25–45
              </span>
            </div>
            <dl className="space-y-3 text-sm" data-id="element-466">
              <div data-id="element-467">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-468">
                  Work
                </dt>
                <dd className="text-film-black" data-id="element-469">
                  Weddings, portraits, editorial, or advanced amateur
                </dd>
              </div>
              <div data-id="element-470">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-471">
                  Setup
                </dt>
                <dd className="text-film-black" data-id="element-472">
                  Multiple bodies and stocks. Lab relationships matter.
                </dd>
              </div>
              <div data-id="element-473">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-474">
                  Jobs
                </dt>
                <dd className="text-film-black" data-id="element-475">
                  Track rolls per job. Push/pull. Which lab has what.
                </dd>
              </div>
              <div data-id="element-476">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-477">
                  Triggers
                </dt>
                <dd className="text-film-black" data-id="element-478">
                  "One system before the next wedding." Rotates labs. Notes
                  under pressure.
                </dd>
              </div>
              <div data-id="element-479">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-480">
                  Creative angle
                </dt>
                <dd className="text-film-black" data-id="element-481">
                  Bag prep, labeling, B-roll + voiceover, before/after.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Voice pillars */}
      <section data-id="element-482">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-483">
          Voice & Tone
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-484">
          Five pillars. Film is slow; we are too.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-id="element-485">
          {[{
          name: 'Calm',
          desc: 'film is slow; we are too'
        }, {
          name: 'Craft-respecting',
          desc: 'defer to the photographer'
        }, {
          name: 'Human',
          desc: 'voice notes over corporate copy'
        }, {
          name: 'Specific',
          desc: 'real stocks, cameras, labs'
        }, {
          name: 'Anti-hype',
          desc: 'no SaaS speak'
        }].map((p, i) => <div key={p.name} className="bg-white border border-film-border p-4" data-id="element-486">
              <p className="font-mono text-[10px] text-film-red mb-2" data-id="element-487">
                0{i + 1}
              </p>
              <p className="font-display font-bold text-film-black mb-1" data-id="element-488">
                {p.name}
              </p>
              <p className="text-xs text-film-gray leading-snug" data-id="element-489">{p.desc}</p>
            </div>)}
        </div>

        {/* Do / Don't */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-490">
          <div className="bg-white border border-film-border p-6" data-id="element-491">
            <div className="flex items-center gap-2 mb-4" data-id="element-492">
              <div className="w-6 h-6 bg-film-black text-white flex items-center justify-center" data-id="element-493">
                <Check className="w-3.5 h-3.5" data-id="element-494" />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-495">
                Do
              </p>
            </div>
            <ul className="space-y-3" data-id="element-496">
              {['Loaded HP5 today.', 'Drop before Friday — Photo Works closes early.', 'Three reasons we love Portra 400.', 'Your workflow, your device.'].map(line => <li key={line} className="text-film-black border-l-2 border-film-black pl-3 text-sm" data-id="element-497">
                  "{line}"
                </li>)}
            </ul>
          </div>
          <div className="bg-white border border-film-border p-6" data-id="element-498">
            <div className="flex items-center gap-2 mb-4" data-id="element-499">
              <div className="w-6 h-6 bg-film-red text-white flex items-center justify-center" data-id="element-500">
                <X className="w-3.5 h-3.5" data-id="element-501" />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-502">
                Don't
              </p>
            </div>
            <ul className="space-y-3" data-id="element-503">
              {['Revolutionize your film photography! 🔥', 'Unlock pro features today!', 'The #1 app for analog creators', 'Emoji-stuffed lines 📸✨🎞️🔥'].map(line => <li key={line} className="text-film-gray border-l-2 border-film-red pl-3 text-sm line-through" data-id="element-504">
                  "{line}"
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Content Categories */}
      <section data-id="element-505">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-506">
          Content Categories
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-507">
          Five labels used across calendars, briefs, and templates.
        </p>
        <div className="space-y-3" data-id="element-508">
          {[{
          code: 'CAT_LAB',
          name: 'Lab Spotlight',
          desc: 'One lab: services, hours quirk, drop-before tip, map snippet, QR. Tag the lab.',
          goal: 'Trust, local share-backs, installs'
        }, {
          code: 'CAT_TIP',
          name: 'Pro Tip',
          desc: 'Short craft or workflow tip. App appears as optional helper, not the lecture.',
          goal: 'Saves, authority'
        }, {
          code: 'CAT_FEATURE',
          name: 'App Feature',
          desc: 'One concrete UI flow (QuickLog, roll states, gear card, lab filter).',
          goal: 'Product clarity'
        }, {
          code: 'CAT_COMMUNITY',
          name: 'Community',
          desc: 'UGC repost (with permission), challenges, comment hours, creator collabs.',
          goal: 'Proof, belonging'
        }, {
          code: 'CAT_GEAR',
          name: 'Gear & Film',
          desc: 'Stock characteristics, X vs Y comparisons, gear deep-dives.',
          goal: 'Discovery, saves'
        }].map(c => <div key={c.code} className="bg-white border border-film-border p-5 grid grid-cols-12 gap-4 items-center" data-id="element-509">
              <div className="col-span-12 md:col-span-3" data-id="element-510">
                <p className="font-mono text-xs text-film-red mb-1" data-id="element-511">{c.code}</p>
                <p className="font-display font-bold text-film-black" data-id="element-512">
                  {c.name}
                </p>
              </div>
              <p className="col-span-12 md:col-span-6 text-sm text-film-gray" data-id="element-513">
                {c.desc}
              </p>
              <p className="col-span-12 md:col-span-3 text-xs text-film-black font-mono" data-id="element-514">
                → {c.goal}
              </p>
            </div>)}
        </div>
      </section>

      {/* Cadence */}
      <section data-id="element-515">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-516">
          Cadence
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-517">
          Quality over volume. Stories carry rhythm; feed carries depth.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-518">
          <div className="bg-white border border-film-border p-6" data-id="element-519">
            <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-2" data-id="element-520">
              Feed
            </p>
            <p className="font-display font-bold text-4xl text-film-black mb-2" data-id="element-521">
              2–3{' '}
              <span className="text-base font-normal text-film-gray" data-id="element-522">
                posts / week
              </span>
            </p>
            <p className="text-sm text-film-gray" data-id="element-523">
              Max 4 in a heavy week. Save/share worthy only — fewer strong
              pieces beat daily filler.
            </p>
          </div>
          <div className="bg-white border border-film-border p-6" data-id="element-524">
            <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-2" data-id="element-525">
              Stories
            </p>
            <p className="font-display font-bold text-4xl text-film-black mb-2" data-id="element-526">
              3–4{' '}
              <span className="text-base font-normal text-film-gray" data-id="element-527">
                on-days / week
              </span>
            </p>
            <p className="text-sm text-film-gray" data-id="element-528">
              Day-on / day-off. 2–5 frames per on-day. Conversation + presence.
            </p>
          </div>
        </div>
        <div className="mt-4 bg-white border border-film-border p-6" data-id="element-529">
          <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-3" data-id="element-530">
            Story Mix
          </p>
          <div className="flex h-8 overflow-hidden border border-film-border" data-id="element-531">
            <div className="bg-film-red text-white text-xs flex items-center justify-center font-mono" style={{
            width: '40%'
          }} data-id="element-532">
              40% RESHARE
            </div>
            <div className="bg-film-orange text-white text-xs flex items-center justify-center font-mono" style={{
            width: '30%'
          }} data-id="element-533">
              30% INTERACTIVE
            </div>
            <div className="bg-film-black text-white text-xs flex items-center justify-center font-mono" style={{
            width: '20%'
          }} data-id="element-534">
              20% BTS
            </div>
            <div className="bg-film-gray text-white text-xs flex items-center justify-center font-mono" style={{
            width: '10%'
          }} data-id="element-535">
              10% UGC
            </div>
          </div>
        </div>
      </section>

      {/* Colors */}
      <section data-id="element-536">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-537">
          Color Palette
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-538">
          Click any swatch to copy hex. Red is reserved for action and urgency.
        </p>

        {/* Primary system */}
        <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-3" data-id="element-539">
          Primary · System
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-10" data-id="element-540">
          <ColorSwatch name="Film Red" hex="#C93B3B" usage="Primary, CTAs" data-id="element-541" />
          <ColorSwatch name="Red Light" hex="#E85D5D" usage="Loaded badges" data-id="element-542" />
          <ColorSwatch name="Orange" hex="#FF8C42" usage="Expiring, warning" data-id="element-543" />
          <ColorSwatch name="Film Black" hex="#2B2B2B" usage="Headings, text" data-id="element-544" />
          <ColorSwatch name="Cream" hex="#F5F5F0" usage="Backgrounds" textColor="text-film-black" data-id="element-545" />
          <ColorSwatch name="Stored Pink" hex="#F5E5E5" usage="Stored badges" textColor="text-film-black" data-id="element-546" />
          <ColorSwatch name="Gray" hex="#9B9B9B" usage="Secondary text" data-id="element-547" />
          <ColorSwatch name="Border" hex="#E8E8E8" usage="Dividers" textColor="text-film-black" data-id="element-548" />
          <ColorSwatch name="Nav Black" hex="#1A1A1A" usage="Navigation" data-id="element-549" />
        </div>

        {/* Tertiary accents */}
        <div className="border-t border-film-border pt-8 mt-2" data-id="element-550">
          <div className="flex items-baseline justify-between mb-1" data-id="element-551">
            <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-552">
              Tertiary · Feature Accents
            </p>
            <p className="font-mono text-[10px] text-film-gray" data-id="element-553">06 hues</p>
          </div>
          <p className="text-sm text-film-gray mb-6 max-w-2xl" data-id="element-554">
            Saturated, analog-confident hues that carry the same weight as
            film-red and film-orange. Drawn from real film-stock packaging and
            darkroom signage. Use for tags, chips, secondary buttons, and
            feature accents — never as primary action.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-id="element-555">
            <ColorSwatch name="Kodak Yellow" hex="#F0B92B" usage="Iconic warmth · CAT_TIP" textColor="text-film-black" data-id="element-556" />
            <ColorSwatch name="Safelight Amber" hex="#D9711F" usage="Push warnings · processing" data-id="element-557" />
            <ColorSwatch name="Darkroom Burgundy" hex="#8B2E3F" usage="Depth state · CAT_LAB hover" data-id="element-558" />
            <ColorSwatch name="Kodachrome Violet" hex="#5B3A82" usage="Community · UGC accents" data-id="element-559" />
            <ColorSwatch name="Fuji Green" hex="#2D7A4F" usage="Loaded · success · ready" data-id="element-560" />
            <ColorSwatch name="Ektar Blue" hex="#2E5C8A" usage="Lab info · technical chips" data-id="element-561" />
          </div>

          {/* Mapping table — what each hue is for */}
          <div className="bg-white border border-film-border overflow-hidden" data-id="element-562">
            <div className="grid grid-cols-12 px-4 py-2.5 border-b border-film-border bg-film-cream" data-id="element-563">
              <div className="col-span-3 font-mono text-[9px] uppercase tracking-wider text-film-gray" data-id="element-564">
                Token
              </div>
              <div className="col-span-2 font-mono text-[9px] uppercase tracking-wider text-film-gray" data-id="element-565">
                Hex
              </div>
              <div className="col-span-3 font-mono text-[9px] uppercase tracking-wider text-film-gray" data-id="element-566">
                App feature
              </div>
              <div className="col-span-4 font-mono text-[9px] uppercase tracking-wider text-film-gray" data-id="element-567">
                Use it for
              </div>
            </div>
            {[{
            name: 'Kodak Yellow',
            hex: '#F0B92B',
            feature: 'Film Stock Info',
            use: 'Stock cards, Portra/HP5 plates, ISO/grain callouts'
          }, {
            name: 'Safelight Amber',
            hex: '#D9711F',
            feature: 'CAT_TIP · Pro Tips',
            use: 'Tip plates, "Pro Tip · N" chips, save-worthy craft posts'
          }, {
            name: 'Darkroom Burgundy',
            hex: '#8B2E3F',
            feature: 'CAT_FEATURE',
            use: 'App-feature posts, news drops, "NEW" chips, changelog'
          }, {
            name: 'Kodachrome Violet',
            hex: '#5B3A82',
            feature: 'CAT_COMMUNITY',
            use: 'UGC reposts, scan credits, community heroes, thank-yous'
          }, {
            name: 'Fuji Green',
            hex: '#2D7A4F',
            feature: 'CAT_GEAR · Gear Info',
            use: 'Camera plates, gear guides, lens specs, loaded status'
          }, {
            name: 'Ektar Blue',
            hex: '#2E5C8A',
            feature: 'CAT_LAB · Labs',
            use: 'Lab spotlights, service chips, hours, lab partnership'
          }].map(c => <div key={c.name} className="grid grid-cols-12 px-4 py-3 border-b border-film-border last:border-b-0 text-sm items-center" data-id="element-568">
                <div className="col-span-3 flex items-center gap-2" data-id="element-569">
                  <div className="w-4 h-4 rounded-sm shrink-0" style={{
                backgroundColor: c.hex
              }} data-id="element-570" />
                  <span className="font-display font-bold text-film-black text-xs" data-id="element-571">
                    {c.name}
                  </span>
                </div>
                <div className="col-span-2 font-mono text-[10px] text-film-gray uppercase" data-id="element-572">
                  {c.hex}
                </div>
                <div className="col-span-3 text-xs text-film-black" data-id="element-573">
                  {c.feature}
                </div>
                <div className="col-span-4 text-xs text-film-gray leading-snug" data-id="element-574">
                  {c.use}
                </div>
              </div>)}
          </div>

          {/* Usage examples */}
          <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mt-10 mb-3" data-id="element-575">
            In use · examples
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-576">
            {/* Example 1: Tag pills */}
            <div className="bg-white border border-film-border p-5" data-id="element-577">
              <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mb-3" data-id="element-578">
                As tag chips
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-579">
                {[{
                label: 'Pro Tip',
                hex: '#F0B92B',
                textDark: true
              }, {
                label: 'Push +1',
                hex: '#D9711F',
                textDark: false
              }, {
                label: 'Sold out',
                hex: '#8B2E3F',
                textDark: false
              }, {
                label: 'Community',
                hex: '#5B3A82',
                textDark: false
              }, {
                label: 'Loaded',
                hex: '#2D7A4F',
                textDark: false
              }, {
                label: 'C-41',
                hex: '#2E5C8A',
                textDark: false
              }].map(t => <span key={t.label} className={`text-[10px] font-mono px-2 py-1 uppercase tracking-wider ${t.textDark ? 'text-film-black' : 'text-white'}`} style={{
                backgroundColor: t.hex
              }} data-id="element-580">
                    {t.label}
                  </span>)}
              </div>
            </div>

            {/* Example 2: Soft containers */}
            <div className="bg-white border border-film-border p-5" data-id="element-581">
              <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mb-3" data-id="element-582">
                As soft containers · 15% opacity
              </p>
              <div className="space-y-1.5" data-id="element-583">
                {[{
                label: 'Loaded · Portra 400',
                hex: '#2D7A4F'
              }, {
                label: 'Push +1 · in progress',
                hex: '#D9711F'
              }, {
                label: 'Pro tip · save this',
                hex: '#F0B92B'
              }].map(row => <div key={row.label} className="flex items-center gap-2 px-2.5 py-1.5" style={{
                backgroundColor: row.hex + '26'
              }} data-id="element-584">
                    <div className="w-1.5 h-1.5 rounded-full" style={{
                  backgroundColor: row.hex
                }} data-id="element-585" />
                    <span className="font-mono text-[10px] text-film-black" data-id="element-586">
                      {row.label}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Example 3: Secondary buttons */}
            <div className="bg-white border border-film-border p-5" data-id="element-587">
              <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mb-3" data-id="element-588">
                As secondary buttons
              </p>
              <div className="space-y-2" data-id="element-589">
                <button className="w-full text-xs font-mono uppercase tracking-wider text-white py-2" style={{
                backgroundColor: '#2E5C8A'
              }} data-id="element-590">
                  Find a lab
                </button>
                <button className="w-full text-xs font-mono uppercase tracking-wider text-white py-2" style={{
                backgroundColor: '#2D7A4F'
              }} data-id="element-591">
                  Mark as loaded
                </button>
                <button className="w-full text-xs font-mono uppercase tracking-wider py-2 border-2 bg-white" style={{
                color: '#8B2E3F',
                borderColor: '#8B2E3F'
              }} data-id="element-592">
                  View archive
                </button>
              </div>
            </div>
          </div>

          {/* Rules */}
          <div className="bg-film-cream border-l-4 border-film-red px-5 py-4 mt-6" data-id="element-593">
            <p className="font-mono text-[9px] uppercase tracking-wider text-film-red mb-2" data-id="element-594">
              Rules
            </p>
            <ul className="space-y-1.5 text-sm text-film-black" data-id="element-595">
              <li className="flex gap-2 items-start" data-id="element-596">
                <span className="text-film-red shrink-0" data-id="element-597">·</span>
                <span data-id="element-598">
                  Tertiaries never replace film-red as the primary CTA.
                </span>
              </li>
              <li className="flex gap-2 items-start" data-id="element-599">
                <span className="text-film-red shrink-0" data-id="element-600">·</span>
                <span data-id="element-601">
                  Pair only one tertiary with red per surface. Stacking them
                  reads chaotic.
                </span>
              </li>
              <li className="flex gap-2 items-start" data-id="element-602">
                <span className="text-film-red shrink-0" data-id="element-603">·</span>
                <span data-id="element-604">
                  At 15–20% opacity they make calm container backgrounds; at
                  100% they make confident tags and buttons.
                </span>
              </li>
              <li className="flex gap-2 items-start" data-id="element-605">
                <span className="text-film-red shrink-0" data-id="element-606">·</span>
                <span data-id="element-607">
                  Always check text contrast — Portra Peach needs dark text; the
                  rest take white.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Typography */}
      <section data-id="element-608">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-609">
          Typography
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-610">
          Futura PT for display. Helvetica Neue for everything else.
        </p>
        <TypographyScale data-id="element-611" />
      </section>

      {/* Logo */}
      <section data-id="element-612">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-613">
          Logo & Marks
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-614">
          Four variants. Choose by background and context.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-615">
          <div className="border border-film-border" data-id="element-616">
            <div className="bg-film-cream p-8 flex items-center justify-center aspect-square" data-id="element-617">
              <img src="https://cdn.magicpatterns.com/uploads/6S42RJUThA4LVcV5MgRYxz/filmnotes-fn-full-icon_1024.png" alt="" className="w-20" data-id="element-618" />
            </div>
            <div className="bg-white p-3 border-t border-film-border" data-id="element-619">
              <p className="font-mono text-[10px] text-film-gray" data-id="element-620">
                Full color · Default
              </p>
            </div>
          </div>
          <div className="border border-film-border" data-id="element-621">
            <div className="bg-film-red p-8 flex items-center justify-center aspect-square" data-id="element-622">
              <img src="https://cdn.magicpatterns.com/uploads/oTP2o8fSQ2isLxJ5nM9Smk/filmnotes-fn-red-icon_1024.png" alt="" className="w-20" data-id="element-623" />
            </div>
            <div className="bg-white p-3 border-t border-film-border" data-id="element-624">
              <p className="font-mono text-[10px] text-film-gray" data-id="element-625">
                Red · On dark
              </p>
            </div>
          </div>
          <div className="border border-film-border" data-id="element-626">
            <div className="bg-film-black p-8 flex items-center justify-center aspect-square" data-id="element-627">
              <img src="https://cdn.magicpatterns.com/uploads/5ceYjttcNKtrLpuLGcysyS/filmnotes-fn-black-icon_1024.png" alt="" className="w-20 invert" data-id="element-628" />
            </div>
            <div className="bg-white p-3 border-t border-film-border" data-id="element-629">
              <p className="font-mono text-[10px] text-film-gray" data-id="element-630">
                Mono · On black
              </p>
            </div>
          </div>
          <div className="border border-film-border" data-id="element-631">
            <div className="bg-film-cream p-8 flex items-center justify-center aspect-square" data-id="element-632">
              <img src="https://cdn.magicpatterns.com/uploads/n83aYeAfPgByVNUDm9vCqj/filmnotes-longtext-icon.png" alt="" className="w-full" data-id="element-633" />
            </div>
            <div className="bg-white p-3 border-t border-film-border" data-id="element-634">
              <p className="font-mono text-[10px] text-film-gray" data-id="element-635">
                Wordmark · Horizontal
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Library */}
      <section data-id="element-636">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-637">
          CTA Library
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-638">
          Approved copy. Rotate; never stack two CTAs in one post.
        </p>
        <div className="flex flex-wrap gap-2" data-id="element-639">
          {['Get the app', 'Link in bio', 'Save this', 'Tag a film friend', 'Find your nearest lab', 'Drop before Friday', 'Read the changelog'].map(cta => <span key={cta} className="bg-film-black text-white px-4 py-2 font-display font-bold text-sm" data-id="element-640">
              {cta}
            </span>)}
        </div>
      </section>

      {/* Legal microcopy */}
      <section data-id="element-641">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-642">
          Legal / UGC Microcopy
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-643">
          Copy-paste templates. Always credit the photographer.
        </p>
        <div className="space-y-3" data-id="element-644">
          {['Shared with permission. Tag us to be featured.', 'Scan: @username · Camera: Nikon FM2 · Film: Portra 400', 'Featured with creator permission. Boost requires written consent.'].map(line => <div key={line} className="bg-film-cream border border-film-border px-4 py-3 font-mono text-xs text-film-black" data-id="element-645">
              {line}
            </div>)}
        </div>
      </section>

      {/* Imagery rules */}
      <section data-id="element-646">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-647">
          Imagery Rules
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-648">
          If it could appear in a stock photo library, reconsider.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-649">
          <div className="bg-white border border-film-border p-6" data-id="element-650">
            <div className="flex items-center gap-2 mb-4" data-id="element-651">
              <div className="w-6 h-6 bg-film-black text-white flex items-center justify-center" data-id="element-652">
                <Check className="w-3.5 h-3.5" data-id="element-653" />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-654">
                Use
              </p>
            </div>
            <ul className="space-y-2 text-sm text-film-black" data-id="element-655">
              <li data-id="element-656">· Real user scans (with permission)</li>
              <li data-id="element-657">· Grain, slight imperfection, natural light</li>
              <li data-id="element-658">· UI screenshots next to scans — don't fake them</li>
              <li data-id="element-659">· Credit line always visible</li>
            </ul>
          </div>
          <div className="bg-white border border-film-border p-6" data-id="element-660">
            <div className="flex items-center gap-2 mb-4" data-id="element-661">
              <div className="w-6 h-6 bg-film-red text-white flex items-center justify-center" data-id="element-662">
                <X className="w-3.5 h-3.5" data-id="element-663" />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-664">
                Avoid
              </p>
            </div>
            <ul className="space-y-2 text-sm text-film-gray" data-id="element-665">
              <li data-id="element-666">· Glossy studio stock photography</li>
              <li data-id="element-667">· Over-saturated colors</li>
              <li data-id="element-668">· Generic camera flat-lays</li>
              <li data-id="element-669">· Fake UI mockups inconsistent with the app</li>
            </ul>
          </div>
        </div>
      </section>
    </div>;
}